import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/i18n/use-translation";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { getAppMetadata } from "@/lib/jwt";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export default function WaitingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);
  const [checking, setChecking] = useState(true);
  const fired = useRef(false);

  // An invited user can land here if the invite-token round-trip was lost in the
  // PKCE email-confirmation flow (POS2-723). Claim any pending invite for their
  // verified email, then route on the *refreshed* org_id claim — not on whether we
  // just accepted something — so an already-accepted account with a stale token
  // also gets in. setSession runs synchronously before navigate, otherwise
  // ProtectedRoute races the async auth-state update, reads the stale token, and
  // bounces back here (and /waiting is under AuthRoute, so we'd be stuck).
  useEffect(() => {
    const token = session?.access_token;
    if (!token || fired.current) return;
    fired.current = true;

    (async () => {
      try {
        await fetch(`${API_URL}/api/v1/invitations/accept-pending`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});

        const { data } = await supabase.auth.refreshSession();
        const claims = data.session ? getAppMetadata(data.session.access_token) : null;
        if (data.session && (claims?.org_id || claims?.is_platform_admin)) {
          setSession(data.session);
          navigate("/", { replace: true });
          return;
        }
      } catch {
        // fall through to the no-organisation message
      }
      setChecking(false);
    })();
  }, [session?.access_token, navigate, setSession]);

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("auth.waitingSettingUp")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.waitingTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{t("auth.waitingDescription")}</p>
          <Button variant="outline" onClick={handleSignOut} className="w-full">
            {t("nav.signOut")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
